import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  View,
  Text,
  Image,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from "react-native";
import { useParams } from "react-router-dom";
import { Ionicons } from "@expo/vector-icons";

/* ==========================================================
   SWIPE SLIDER – modern, tactile
========================================================== */
const SwipeSlider = ({ onSwipeComplete }) => {
  const [dragX, setDragX] = useState(0);
  const sliderWidth = 320;
  const knobWidth = 64;

  const handleTouchMove = (e) => {
    let clientX =
      e.touches?.[0]?.clientX ??
      e.changedTouches?.[0]?.clientX ??
      e.clientX ??
      0;

    const sliderLeft = e.currentTarget.getBoundingClientRect().left;
    let newX = clientX - sliderLeft - knobWidth / 2;
    if (newX < 0) newX = 0;
    if (newX > sliderWidth - knobWidth) newX = sliderWidth - knobWidth;

    setDragX(newX);

    if (newX >= sliderWidth - knobWidth) {
      onSwipeComplete();
    }
  };

  return (
    <View
      style={{
        width: sliderWidth,
        height: 64,
        backgroundColor: "#0E0E0E",
        borderRadius: 999,
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        borderWidth: 1,
        borderColor: "rgba(255,255,255,0.08)",
      }}
      onTouchMove={handleTouchMove}
      onMouseMove={(e) => e.buttons === 1 && handleTouchMove(e)}
    >
      <Text
        style={{
          color: "#FDF05D",
          fontWeight: "800",
          textAlign: "center",
          position: "absolute",
          width: "100%",
          zIndex: 1,
          fontSize: 16,
          letterSpacing: 0.3,
        }}
      >
        Swipe to Redeem →
      </Text>

      <View
        style={{
          width: knobWidth,
          height: 64,
          borderRadius: 999,
          backgroundColor: "#FDF05D",
          position: "absolute",
          left: dragX,
          zIndex: 2,
          alignItems: "center",
          justifyContent: "center",
          shadowColor: "#000",
          shadowOpacity: 0.15,
          shadowRadius: 8,
          shadowOffset: { width: 0, height: 4 },
        }}
      >
        <Ionicons name="chevron-forward" size={24} color="#000" />
      </View>
    </View>
  );
};

/* ==========================================================
   CAMERA MODAL – one-tap capture (tap video or button)
========================================================== */
const CameraModal = ({ visible, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (visible && Platform.OS === "web") startCamera();
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (e) {
      onClose();
      alert("Camera blocked — enable permissions.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const captureImage = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64 = canvas.toDataURL("image/jpeg", 0.9); // slightly higher quality
    stopCamera();
    onCapture(base64);
  };

  if (!visible) return null;

  return (
    <View
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.85)",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 16,
      }}
    >
      <View
        style={{
          width: "100%",
          maxWidth: 420,
          backgroundColor: "white",
          borderRadius: 16,
          padding: 16,
          borderWidth: 1,
          borderColor: "#eee",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            justifyContent: "center",
          }}
        >
          <Ionicons name="camera" size={18} color="#000" />
          <Text style={{ fontSize: 18, fontWeight: "800" }}>
            Take a quick photo inside
          </Text>
        </View>

        <div
          onClick={captureImage}
          style={{
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <video
            ref={videoRef}
            playsInline
            muted
            style={{
              width: "100%",
              backgroundColor: "#000",
              borderRadius: 12,
              marginVertical: 14,
              maxHeight: 420,
              objectFit: "cover",
            }}
            // One-tap anywhere on the video to capture
            onClick={captureImage}
          />
        </div>

        <View style={{ flexDirection: "row", gap: 12 }}>
          <TouchableOpacity
            onPress={onClose}
            style={{
              flex: 1,
              borderWidth: 1,
              borderColor: "#ddd",
              padding: 12,
              borderRadius: 10,
              alignItems: "center",
            }}
          >
            <Text>Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={captureImage}
            style={{
              flex: 1,
              padding: 12,
              borderRadius: 10,
              backgroundColor: "#000",
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <Ionicons name="aperture" size={18} color="#FDF05D" />
            <Text style={{ color: "#FDF05D", fontWeight: "800" }}>
              Capture
            </Text>
          </TouchableOpacity>
        </View>

        <Text
          style={{
            textAlign: "center",
            fontSize: 12,
            marginTop: 8,
            opacity: 0.6,
          }}
        >
          Tap the preview to capture instantly.
        </Text>
      </View>
    </View>
  );
};

/* ==========================================================
   FINAL VOUCHER SCREEN – premium card
========================================================== */
const FinalVoucherScreen = ({ brand, offer, redeemCode, expiresIn }) => (
  <View
    style={{
      backgroundColor: "#FDF05D",
      flex: 1,
      alignItems: "center",
      paddingTop: 80,
      paddingBottom: 40,
    }}
  >
    <Image
      source={require("./CollabbayLogoDark.png")}
      style={{
        width: 40,
        height: 40,
        borderRadius: 20,
        position: "absolute",
        top: 20,
      }}
    />

    <Text style={{ fontSize: 26, fontWeight: "900" }}>{brand}</Text>

    <View
      style={{
        backgroundColor: "#fff",
        width: "92%",
        maxWidth: 440,
        padding: 22,
        borderRadius: 18,
        marginTop: 20,
        borderWidth: 1,
        borderColor: "#eee",
        alignItems: "center",
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 6 },
      }}
    >
      <View
        style={{
          backgroundColor: "#E8FFF0",
          borderColor: "#B9F2CB",
          borderWidth: 1,
          paddingVertical: 6,
          paddingHorizontal: 12,
          borderRadius: 999,
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
        }}
      >
        <Ionicons name="shield-checkmark" size={16} color="#16A34A" />
        <Text style={{ color: "#166534", fontWeight: "700", fontSize: 12 }}>
          Active
        </Text>
      </View>

      <Text
        style={{
          fontSize: 30,
          fontWeight: "900",
          textAlign: "center",
          marginTop: 12,
          lineHeight: 34,
        }}
      >
        {offer}
      </Text>

      <Text style={{ marginTop: 16, fontWeight: "900", fontSize: 18 }}>
        Code: {redeemCode}
      </Text>

      <Text style={{ opacity: 0.6, marginTop: 10 }}>
        Expires in {expiresIn}s
      </Text>

      <Text
        style={{
          marginTop: 14,
          fontSize: 12,
          opacity: 0.6,
          textAlign: "center",
        }}
      >
        Show this screen at the till to redeem.
      </Text>
    </View>
  </View>
);

/* ==========================================================
   MAIN VIEWTRACKER – fully centred, pre-swipe UX improved
========================================================== */
const ViewTracker = () => {
  const { CollabID } = useParams();

  const [collabData, setCollabData] = useState(null);
  const [offerData, setOfferData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [swiped, setSwiped] = useState(false);
  const [cameraVisible, setCameraVisible] = useState(false);
  const [finalUnlocked, setFinalUnlocked] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [expiresIn, setExpiresIn] = useState(300);

  const [debugBox, setDebugBox] = useState("");

  /* ---------- FETCH DATA ---------- */
  useEffect(() => {
    (async () => {
      try {
        const a = await fetch(
          `https://collabbay.com/APIControllers/RetrieveCollabLandingPage.php?CollabID=${CollabID}`
        );
        const cd = await a.json();
        setCollabData(cd);

        const b = await fetch(
          `https://collabbay.com/APIControllers/RetrieveOfferDescription.php?ListingID=${cd.ListingID}`
        );
        const od = await b.json();
        setOfferData(od);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [CollabID]);

  const isVoucher = useMemo(() => {
    return (
      offerData &&
      offerData.CampaignType &&
      String(offerData.CampaignType).toLowerCase() === "voucher"
    );
  }, [offerData]);

  /* ---------- UNLOCK TIMER ---------- */
  useEffect(() => {
    if (!finalUnlocked) return;
    const iv = setInterval(() => {
      setExpiresIn((t) => (t > 1 ? t - 1 : 0));
    }, 1000);
    return () => clearInterval(iv);
  }, [finalUnlocked]);

  /* ==========================================================
     PHOTO CAPTURE → FILE → PHP FORM UPLOAD (unchanged)
  ========================================================== */
  const handlePhotoCaptured = async (base64) => {
    setCameraVisible(false);

    try {
      // convert base64 → file
      const byteString = atob(base64.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

      const file = new File([new Blob([ab], { type: "image/jpeg" })], `voucher_${Date.now()}.jpg`, {
        type: "image/jpeg",
      });

      // submit to PHP
      const fd = new FormData();
      fd.append("CollabID", collabData.CollabID);
      fd.append("ListingID", collabData.ListingID);
      fd.append("BrandName", offerData.BrandName);
      fd.append("Promoter", collabData.Promoter);
      fd.append("AssociatedRevenue", offerData.AssociatedRevenue);
      fd.append("PublicOffer", offerData.PublicOffer);
      fd.append("image", file);

      const res = await fetch(
        "https://collabbay.com/APIControllers/CustomerVoucherRedeemInsert.php",
        { method: "POST", body: fd }
      );

      const raw = await res.text();

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        setDebugBox(raw);
        alert("Server returned raw response — scroll for details.");
        return;
      }

      if (!parsed.success) {
        alert(parsed.message || "Redeem failed");
        setDebugBox(JSON.stringify(parsed, null, 2));
        return;
      }

      setRedeemCode(parsed.redemption_code);
      setFinalUnlocked(true);
    } catch (err) {
      alert("Failed to redeem");
      console.error(err);
    }
  };

  /* ---------- RENDER ---------- */
  if (loading)
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator size="large" color="#000" />
      </View>
    );

  if (isVoucher && finalUnlocked) {
    return (
      <FinalVoucherScreen
        brand={offerData.BrandName}
        offer={offerData.PublicOffer}
        redeemCode={redeemCode}
        expiresIn={expiresIn}
      />
    );
  }

  /* ---------- Voucher Page (pre-swipe) ---------- */
  if (isVoucher) {
    const promoter = collabData?.Promoter || "You";
    const brand = offerData?.BrandName || "";

    return (
      <ScrollView
        contentContainerStyle={{
          backgroundColor: "#FDF05D",
          minHeight: "100%",
          alignItems: "center",
          paddingVertical: 32,
        }}
      >
        {/* Top branding */}
        <Image
          source={require("./CollabbayLogoDark.png")}
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            marginTop: 16,
            marginBottom: 10,
          }}
        />

        {/* Promoter × Brand badge */}
        <View
          style={{
            backgroundColor: "#121212",
            borderRadius: 999,
            paddingVertical: 8,
            paddingHorizontal: 14,
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.08)",
          }}
        >
          <Ionicons name="person" size={14} color="#FDF05D" />
          <Text style={{ color: "white", fontWeight: "700", fontSize: 12 }}>
            {promoter} × {brand}
          </Text>
        </View>

        {/* “At the venue now?” banner */}
        <View
          style={{
            marginTop: 12,
            backgroundColor: "white",
            borderRadius: 10,
            paddingVertical: 8,
            paddingHorizontal: 12,
            borderWidth: 1,
            borderColor: "#eee",
          }}
        >
          <Text style={{ fontWeight: "800" }}>At the venue now?</Text>
          <Text style={{ opacity: 0.7, fontSize: 12 }}>
            Swipe and take a quick photo inside to unlock your voucher.
          </Text>
        </View>

        {/* Wallet-style voucher card */}
        <View
          style={{
            backgroundColor: "white",
            width: "92%",
            maxWidth: 460,
            padding: 20,
            borderRadius: 18,
            borderWidth: 1,
            borderColor: "#eee",
            marginTop: 16,
            alignItems: "center",
            shadowColor: "#000",
            shadowOpacity: 0.08,
            shadowRadius: 12,
            shadowOffset: { width: 0, height: 6 },
          }}
        >
          <View
            style={{
              backgroundColor: "#0E0E0E",
              paddingVertical: 6,
              paddingHorizontal: 10,
              borderRadius: 999,
              alignSelf: "center",
              marginBottom: 8,
            }}
          >
            <Text style={{ color: "#FDF05D", fontSize: 12, fontWeight: "800" }}>
              Voucher
            </Text>
          </View>

          <Text
            style={{
              fontSize: 28,
              fontWeight: "900",
              textAlign: "center",
              marginTop: 2,
              lineHeight: 32,
            }}
          >
            {offerData.PublicOffer}
          </Text>

          {/* Eligibility chip (visible after swipe trigger) */}
          {swiped && !finalUnlocked && (
            <View
              style={{
                marginTop: 12,
                backgroundColor: "#E8FFF0",
                borderColor: "#B9F2CB",
                borderWidth: 1,
                paddingVertical: 6,
                paddingHorizontal: 12,
                borderRadius: 999,
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Ionicons name="checkmark-circle" size={16} color="#16A34A" />
              <Text style={{ color: "#166534", fontWeight: "700", fontSize: 12 }}>
                Eligible — take a quick photo
              </Text>
            </View>
          )}

          {/* Swipe CTA */}
          {!swiped && (
            <View style={{ marginTop: 18 }}>
              <SwipeSlider
                onSwipeComplete={() => {
                  setSwiped(true);
                  setCameraVisible(true);
                }}
              />
            </View>
          )}

          {/* Camera opening hint */}
          {swiped && !finalUnlocked && !cameraVisible && (
            <Text style={{ opacity: 0.6, marginTop: 16 }}>
              Opening camera…
            </Text>
          )}

          {/* Till subtext */}
          <Text
            style={{
              marginTop: 16,
              fontSize: 12,
              opacity: 0.6,
              textAlign: "center",
            }}
          >
            Present the unlocked voucher at the till.
          </Text>
        </View>

        {/* CAMERA MODAL */}
        <CameraModal
          visible={cameraVisible}
          onClose={() => setCameraVisible(false)}
          onCapture={handlePhotoCaptured}
        />

        {/* DEBUG BOX (shows raw PHP if any) */}
        {debugBox !== "" && (
          <View
            style={{
              width: "92%",
              maxWidth: 460,
              backgroundColor: "#fff3cd",
              padding: 15,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: "#ffeeba",
              marginTop: 18,
            }}
          >
            <Text style={{ fontWeight: "bold", color: "#856404" }}>
              RAW PHP OUTPUT
            </Text>
            <Text style={{ marginTop: 8, color: "#856404" }}>{debugBox}</Text>
          </View>
        )}

        {/* Bottom breathing space */}
        <View style={{ height: 80 }} />
      </ScrollView>
    );
  }

  /* ---------- NON-VOUCHER DEFAULT ---------- */
  return (
    <ScrollView
      contentContainerStyle={{
        paddingVertical: 60,
        alignItems: "center",
      }}
    >
      <Text>Not a voucher campaign.</Text>
    </ScrollView>
  );
};

export default ViewTracker;
